import React from 'react';
const { useState } = React;
import { coachCall } from './shared.jsx';
import { METRIC_COUNTS } from '../../journey.js';

// The progress indicators of Mi recorrido, for the coach's screens (Personas and a team's «Progreso»). The numbers
// come from the server, computed with the same rules the person sees (personSpace.spaceView, src/journey.js): these
// pieces never compute a permission nor invent a number. Every indicator says what it counts; color is never the
// only signal. Each action opens exactly the session, submission or conversation, and uses the same notices as
// «Por atender» (CoachAttention.jsx): acting here or there clears both, no new state.

export const muted = { fontSize: 12, color: 'var(--globant-muted)' };
const TRACK = '#D6E2DF';

/** «3 de 8» as a ring; nothing without a defined total. */
export function Ring({ metric, size = 48 }) {
  if (!metric) return null;
  const stroke = 6, r = (size - stroke) / 2, c = 2 * Math.PI * r, ratio = metric.total ? metric.done / metric.total : 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={metric.label} style={{ flex: 'none' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TRACK} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--globant-green)" strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - ratio)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize={size < 44 ? 10 : 12} fontWeight="700" fill="currentColor" aria-hidden="true">{metric.done}/{metric.total}</text>
    </svg>
  );
}

/** A bar with its words, and (optionally) what it counts. */
export function Bar({ metric, title, explain = false }) {
  if (!metric) return null;
  const pct = Math.round((metric.done / metric.total) * 100);
  return (
    <div style={{ display: 'grid', gap: 3, fontSize: 13 }}>
      {title && <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--globant-muted)' }}>{title}</span>}
      <div role="progressbar" aria-label={title || metric.label} aria-valuemin={0} aria-valuemax={metric.total} aria-valuenow={metric.done} aria-valuetext={metric.label}
        style={{ height: 7, borderRadius: 5, background: TRACK, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--globant-green)', borderRadius: 5 }} />
      </div>
      <span>{metric.label}</span>
      {explain && METRIC_COUNTS[metric.unit] && <span style={muted}>{METRIC_COUNTS[metric.unit]}</span>}
    </div>
  );
}

/** Ring + its label + what it counts. */
export function MainMetric({ metric, title, size = 52 }) {
  if (!metric) return null;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
      <Ring metric={metric} size={size} />
      <div style={{ minWidth: 0 }}>
        {title && <b style={{ display: 'block' }}>{title}</b>}
        <div style={{ fontSize: 13 }}>{metric.label}</div>
        {METRIC_COUNTS[metric.unit] && <div style={muted}>{METRIC_COUNTS[metric.unit]}</div>}
      </div>
    </div>
  );
}

const STEP = { done: ['✓', 'Hecho'], current: ['●', 'Ahora'], ahead: ['', 'Más adelante'] };
export function Milestones({ steps = [], title = 'Hitos' }) {
  if (!steps.length) return null;
  return (
    <ol aria-label={title} style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
      {steps.map(s => (
        <li key={s.key} aria-current={s.state === 'current' ? 'step' : undefined} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
          <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 9, flex: 'none', display: 'grid', placeItems: 'center', fontSize: 10, border: `2px solid ${s.state === 'ahead' ? TRACK : 'var(--globant-green)'}`, background: s.state === 'done' ? 'var(--globant-green)' : 'transparent', color: s.state === 'done' ? '#FFFFFF' : 'var(--globant-green)' }}>{STEP[s.state][0]}</span>
          <span><b style={{ fontWeight: 600 }}>{s.title}</b> <span style={muted}>· {STEP[s.state][1]}: {s.detail}{s.at ? ` · ${fmtWhen(s.at)}` : ''}</span></span>
        </li>
      ))}
    </ol>
  );
}

export const fmtWhen = (iso, tz) => { try { return new Date(iso).toLocaleString('es', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', ...(tz ? { timeZone: tz } : {}) }); } catch { return iso; } };
export const fmtDay = (d) => { try { return new Date(`${String(d).slice(0, 10)}T12:00:00Z`).toLocaleDateString('es', { day: 'numeric', month: 'short' }); } catch { return d; } };

// ── Opening exactly what each pending item is about (the same paths as «Por atender») ──
const openCoaching = (detail) => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'coaching', ...detail } }));
export const openSession = (processId, sessionId) => openCoaching({ processId, at: { tab: 'sessions', session: sessionId } });
export const openActivities = (processId) => openCoaching({ processId, at: { tab: 'activities' } });
export const openReview = (r) => openCoaching({ section: 'learning', at: { enrollmentId: r.enrollmentId, lessonId: r.lessonId } });
/** The conversation: its notice is marked read (the same one «Por atender» shows), then the thread opens. */
export async function openThread(m) {
  try { await coachCall({ action: 'markNotificationsRead', ids: [m.id] }); } catch { /* opening still works */ }
  openCoaching({ processId: m.processId, at: { tab: 'messages', thread: m.channel === 'group' ? 'group' : m.threadMemberId } });
}

/** «Ya la agendé» (the same action and mark as «Por atender»). */
export function ScheduledButton({ sessionId, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  return (
    <>
      <button type="button" className="action-btn btn-primary" style={{ fontSize: 11, padding: '3px 10px' }} disabled={busy}
        onClick={async () => { setBusy(true); setErr(''); try { await coachCall({ action: 'markCalendarAdded', sessionId }); onDone?.(); } catch (e) { setErr(e.message); } setBusy(false); }}>{busy ? 'Guardando…' : 'Ya la agendé'}</button>
      {err && <span role="alert" style={{ ...muted, color: '#9A6212' }}>{err}</span>}
    </>
  );
}
